import React, { useEffect, useState } from "react";
import { ukDayYear } from "@/lib/time";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { History, FileDown, Loader2, ShieldCheck, Clock, ArrowRight, ChevronDown, Archive, Mail, MailCheck } from "lucide-react";

const fmtDate = (iso) => ukDayYear(iso, "—");

const STATUS_META = {
  signed:   { label: "Executed",   cls: "border-teal-200 bg-teal-50 text-teal-700", Icon: ShieldCheck },
  sent:     { label: "Awaiting signature", cls: "border-[#7da9ff]/45 bg-[#7da9ff]/[0.10] text-[#3B63A8]", Icon: Clock },
  not_sent: { label: "Not issued", cls: "border-slate-200 bg-slate-50 text-slate-500", Icon: Clock },
};

function VersionRow({ v, onDownload, busy, superseded }) {
  const st = STATUS_META[v.status] || STATUS_META.not_sent;
  return (
    <div className={`relative pl-7 pb-4 last:pb-0 ${superseded ? "opacity-90" : ""}`} data-testid={`terms-history-version-${v.version}`}>
      <span className="absolute left-0 top-1 grid place-items-center size-4 rounded-full border-2 border-white ring-1 ring-slate-200 bg-white">
        <span className={`size-2 rounded-full ${v.current ? "bg-[#0055FF]" : v.status === "signed" ? "bg-teal-500" : "bg-slate-300"}`} />
      </span>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[13px] font-semibold text-slate-900">Version {v.version}</span>
        {v.current
          ? <span className="rounded-full border border-[#0055FF]/30 bg-[#0055FF]/[0.07] text-[#0055FF] px-2 h-5 inline-flex items-center text-[10px] font-semibold tracking-wide">CURRENT</span>
          : <span className="rounded-full border border-slate-200 bg-slate-50 text-slate-500 px-2 h-5 inline-flex items-center gap-1 text-[10px] font-semibold tracking-wide"><Archive className="size-2.5" /> SUPERSEDED</span>}
        <span className={`rounded-full border px-2 h-5 inline-flex items-center gap-1 text-[10px] font-semibold ${st.cls}`}>
          <st.Icon className="size-2.5" /> {st.label}
        </span>
      </div>
      <div className="mt-1 text-[13px] text-slate-800">
        {v.rate ? <><b className="tabular-nums">{v.rate}</b>{v.short ? <span className="text-slate-500"> · {v.short}</span> : null}</>
                : <span className="text-slate-400">Rate not recorded on this version</span>}
      </div>
      <div className="mt-1 text-[11.5px] text-slate-400 leading-relaxed">
        {v.sent_at ? `Issued ${fmtDate(v.sent_at)}` : "Not issued"}
        {v.signed_at ? ` · Signed ${fmtDate(v.signed_at)}${v.signed_name ? ` by ${v.signed_name}` : ""}${v.method === "wet_ink" ? " (wet ink)" : ""}` : ""}
        {v.superseded_at ? ` · Superseded ${fmtDate(v.superseded_at)}` : ""}
      </div>
      <Button variant="outline" size="sm" onClick={() => onDownload(v.version)} disabled={busy === v.version}
              className="mt-2 h-8 rounded-full text-[11.5px]" data-testid={`terms-history-dl-${v.version}`}>
        {busy === v.version ? <Loader2 className="size-3.5 animate-spin" /> : <><FileDown className="size-3 mr-1.5" /> Download v{v.version}</>}
      </Button>
    </div>
  );
}

/**
 * TermsHistory — every version of a partner agreement with the rate it carried and its dates,
 * plus a log of rate changes made between re-issues. Used by the Hub and the partner portals.
 */
export function TermsHistory({ historyPath, versionPath, notifyPath, doc = "agreement", reloadKey, defaultOpen = false }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(defaultOpen);
  const [busy, setBusy] = useState(null);
  const [notifying, setNotifying] = useState(false);

  const load = () => api.get(historyPath, { params: { doc } }).then((r) => setData(r.data)).catch(() => setData(null));

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(historyPath, { params: { doc } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, [historyPath, doc, reloadKey]);

  const notify = async () => {
    setNotifying(true);
    try {
      const r = await api.post(notifyPath);
      toast.success(`New terms emailed to the partner (${r.data.rate})`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the notice"); }
    finally { setNotifying(false); }
  };

  const download = async (version) => {
    setBusy(version);
    try {
      const r = await api.get(versionPath(version), { params: { doc, _ts: Date.now() }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `connexis-${doc}-v${version}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("That version could not be downloaded"); }
    finally { setBusy(null); }
  };

  if (!data) return null;
  const versions = data.items || [];
  const changes = data.changes || [];
  if (versions.length <= 1 && changes.length === 0) return null;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="terms-history">
      <button onClick={() => setOpen((o) => !o)} data-testid="terms-history-toggle"
              className="w-full flex items-center gap-2.5 px-4 h-12 text-left hover:bg-slate-50/80 transition-colors">
        <span className="grid place-items-center size-7 rounded-lg border border-slate-200 bg-slate-50 text-slate-600"><History className="size-3.5" /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-slate-900">Terms history</span>
          <span className="block text-[11px] text-slate-400">
            {versions.length} version{versions.length === 1 ? "" : "s"}
            {changes.length ? ` · ${changes.length} rate change${changes.length === 1 ? "" : "s"}` : ""}
          </span>
        </span>
        <ChevronDown className={`size-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-4 border-t border-slate-100" data-testid="terms-history-body">
          <div className="mt-3.5 relative before:absolute before:left-[7px] before:top-2 before:bottom-2 before:w-px before:bg-slate-200">
            {versions.map((v) => (
              <VersionRow key={v.version} v={v} onDownload={download} busy={busy} superseded={!v.current} />
            ))}
          </div>

          {changes.length > 0 && (
            <div className="mt-3 pt-3 border-t border-slate-100" data-testid="terms-history-changes">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">Rate changes</div>
              <div className="mt-2 space-y-1.5">
                {changes.map((c, i) => (
                  <div key={`${c.at}-${i}`} className="flex items-center gap-2 flex-wrap text-[12px] text-slate-600"
                       data-testid={`terms-history-change-${i}`}>
                    <span className="tabular-nums font-semibold text-slate-500">{c.from?.rate || "—"}</span>
                    <ArrowRight className="size-3 text-slate-300" />
                    <span className="tabular-nums font-semibold text-slate-900">{c.to?.rate || "—"}</span>
                    <span className="text-slate-400">· {fmtDate(c.at)}</span>
                    {c.version ? <span className="text-slate-300">· while on v{c.version}</span> : null}
                    {c.by ? <span className="text-slate-300">· {c.by}</span> : null}
                    {notifyPath && (
                      c.notified_at ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-teal-200 bg-teal-50 text-teal-700 px-2 h-5 text-[10px] font-semibold"
                              data-testid={`terms-history-notified-${i}`}>
                          <MailCheck className="size-2.5" /> Partner told {fmtDate(c.notified_at)}
                        </span>
                      ) : i === 0 ? (
                        <Button variant="outline" size="sm" onClick={notify} disabled={notifying}
                                className="h-6 rounded-full text-[10.5px] px-2" data-testid="terms-history-notify">
                          {notifying ? <Loader2 className="size-3 animate-spin" /> : <><Mail className="size-2.5 mr-1" /> Tell them now</>}
                        </Button>
                      ) : (
                        <span className="text-slate-300 text-[10.5px]">not emailed</span>
                      )
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-2.5 text-[11px] text-slate-400 leading-relaxed">
                An executed agreement always keeps the rate it was signed at — a change only takes effect on paper once a new version is issued and countersigned.
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
